import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  EventEmitter,
  Output,
  TemplateRef,
  ViewContainerRef,
  SimpleChanges,
  Input,
  ElementRef,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, Observable, of, Subject, Subscription, takeUntil } from 'rxjs';
import { RefreshGridService } from '../../../shared/services/refresh-grid.service'; // Import the shared service
import { EngagementService } from '../../../services/engagement.service';
import {
  GridModule,
  CellClickEvent,
  DataBindingDirective,
  IdService,
} from '@progress/kendo-angular-grid';
import { Border, ChartsModule } from '@progress/kendo-angular-charts';
import { InputsModule, SliderModule } from '@progress/kendo-angular-inputs';
import {
  KENDO_LAYOUT,
  LayoutModule,
  SelectEvent,
} from '@progress/kendo-angular-layout';
import { ScrollViewModule } from '@progress/kendo-angular-scrollview';
import {
  KENDO_ICONS,
  IconsModule,
  SVGIcon,
} from '@progress/kendo-angular-icons';
import { KENDO_BUTTONS, ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { AutoCompleteComponent, DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { GaugesModule } from '@progress/kendo-angular-gauges';
import { IntlModule } from '@progress/kendo-angular-intl';
import { PopupService, PopupRef } from '@progress/kendo-angular-popup';
import {
  starIcon,
  checkCircleIcon,
  envelopIcon,
  plusIcon,
  eyeIcon,
  minusIcon,
  starOutlineIcon,
  cancelCircleIcon,
  brightnessContrastIcon,
  searchIcon,
  playIcon,
  arrowDownIcon,
  caretDoubleAltDownIcon,
  caretAltDownIcon,
} from '@progress/kendo-svg-icons';
import { process } from '@progress/kendo-data-query';
import { CommonModule } from '@angular/common';
import { PopupModule, Align, AnimationDirection, AnimationType, PopupAnimation } from '@progress/kendo-angular-popup';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ShownotificationService } from '../../../services/shownotification.service';

@Component({
  selector: 'app-remove-engagement',
  standalone: true,
  templateUrl: './remove-engagement.component.html',
  styleUrl: './remove-engagement.component.scss',
  imports: [
    KENDO_LAYOUT,
    KENDO_ICONS,
    KENDO_BUTTONS,
    GridModule,
    ChartsModule,
    InputsModule,
    SliderModule,
    LayoutModule,
    ScrollViewModule,
    IconsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    ListViewModule,
    FormsModule,
    ReactiveFormsModule,
    GaugesModule,
    IntlModule,
    PopupModule,
    CommonModule
  ]
})
export class RemoveEngagementComponent implements OnInit, OnDestroy {
  @Input() openEngRemovalRequestPopup!: boolean;
  @Input() engRemovalData: any;
  @Input() anchor: any;
  @Output() closeEvent = new EventEmitter<void>();

  @ViewChild('popupContent') popupContent!: TemplateRef<any>;
  private popupRef: PopupRef | undefined;
  @ViewChild("autocomplete", { static: false }) public autocomplete!: AutoCompleteComponent;

  public searchIcon: SVGIcon = searchIcon;
  public caretAltDownIcon: SVGIcon = caretAltDownIcon;
  public engRemovalRequestDetailsForm!: FormGroup;
  public enabled = true;
  public duration = 200;
  public type: AnimationType = "slide";
  public direction: AnimationDirection = "left";
  public margin = { horizontal: -100, vertical: -280 };
  public anchorAlign: Align = { horizontal: "right", vertical: "top" };
  public popupAlign: Align = { horizontal: "right", vertical: "top" };

  public showFinalConfirmation = false;
  public engCancellationForm!: FormGroup;
  public isConfirmedTextEng: boolean = false;
  private destroy$ = new Subject<void>();

  public get animate(): boolean | PopupAnimation {
    if (this.enabled) {
      return {
        type: this.type,
        direction: this.direction,
        duration: this.duration,
      };
    }
    return false;
  }

  public defaultItem: any = {
    id: 3,
    userRole: 'Engagement User'
  };

  public selectedEngagement: any = null;
  public engagementName!: string;
  public loggedInUserRole = '';
  public modulesExist = false;
   public usersExist = false;

  private readonly popupService = inject(PopupService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly shownotificationService = inject(ShownotificationService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly engagementService = inject(EngagementService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['engRemovalData']) {
      this.modulesExist = this.engRemovalData?.modules?.length > 0;
      //this.usersExist = this.engRemovalData?.users?.length > 0;
      if (this.engRemovalData?.email && this.engRemovalData?.users) {
        const contactEmail = this.engRemovalData.email;

        // Filter out the user with the matching email
        this.engRemovalData.users = this.engRemovalData.users.filter((x: { email: any; }) => x.email !== contactEmail);
      }

      // Check the length condition
      this.usersExist = this.engRemovalData?.users?.length > 0;
    }
  }

  ngOnInit(): void {
    // Initialization code if necessary
    this.engCancellationForm = this.fb.group({
      confirmDeleteEngText: ['']
    });
  }

  onInputChange() {
    const inputText = this.engCancellationForm.get('confirmDeleteEngText')?.value;
    this.isConfirmedTextEng = (inputText === 'Delete Client Engagement');
  }

  ngOnDestroy(): void {
    // Cleanup code if necessary
    this.destroy$.next();
    this.destroy$.complete();
  }
  public open(): void {
    this.openEngRemovalRequestPopup = true;

  }
  public close(): void {
    this.openEngRemovalRequestPopup = false;
    this.closeEvent.emit();
    this.resetState();
  }

  public handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.close();
    }
  };

  private resetState(): void {
    this.engCancellationForm.reset();
    this.modulesExist = false;
    this.usersExist = false;
    this.showFinalConfirmation = false;
    this.isConfirmedTextEng = false;
  }

  public processEngRemovalRequest(): void {
    const engagementId = this.engRemovalData?.engagementId;
    const modules = this.engRemovalData?.modules;
    const users = this.engRemovalData?.users;

    // Check if the engagement has any modules
    if ((modules && modules.length > 0) || (users && users.length > 0)) {
      console.error('Engagement cannot be deleted as it contains modules or users');
      this.shownotificationService.showNotification('errorEngHasModules');
      return; // Exit the function to prevent the deletion
    }

    if (engagementId) {
      this.engagementService.deleteEngagement(engagementId).
      pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          this.shownotificationService.showNotification('engRemovedSuccess');
          this.close();
        },
        error => {
          console.error('Error deleting engagement', error);
          this.shownotificationService.showNotification('errorEngRemove');
        }
      );
    } else {
      console.error('Engagement Id is missing');
      this.shownotificationService.showNotification('errorEngIdMissing');
    }
    this.close();
  }

  public cancelEngRemovalRequest(): void {
    this.close();
  }
}
